import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  FileText,
  Link2,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

export default function LeadDocumentsPanel({ leadId }) {
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState({});
  const [category, setCategory] = useState("other");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [token, setToken] = useState(null);
  const [sharing, setSharing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/leads/${leadId}/documents`);
      setDocs(data.documents || []);
      setCategories(data.categories || {});
      setToken(data.upload_token || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const onPick = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File must be under 15MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      await api.post(`/admin/leads/${leadId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/admin/leads/${leadId}/documents/${doc.id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/admin/leads/${leadId}/documents/${doc.id}`);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const share = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/documents/share`);
      setToken(data.token);
      const url = `${window.location.origin}/upload-docs/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Student upload link copied");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSharing(false);
    }
  };

  const shareUrl = token ? `${window.location.origin}/upload-docs/${token}` : null;

  return (
    <div data-testid="lead-documents-panel">
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-medium text-slate-600">Documents</label>
        <span className="text-[11px] text-slate-400">{docs.length} file(s)</span>
      </div>

      {/* Upload row */}
      <div className="flex items-center gap-2">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-9 flex-1 text-xs" data-testid="doc-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(categories).map(([k, label]) => (
              <SelectItem key={k} value={k} data-testid={`doc-cat-${k}`}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid="doc-file-input"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          data-testid="doc-upload-btn"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Share link */}
      <div className="mt-2 flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={share}
          disabled={sharing}
          className="text-[#1877F2] hover:bg-blue-50"
          data-testid="doc-share-btn"
        >
          {sharing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Link2 className="mr-1.5 h-3.5 w-3.5" />}
          {token ? "Copy student upload link" : "Create student upload link"}
        </Button>
      </div>
      {shareUrl && (
        <p className="mt-1 truncate text-[10px] text-slate-400" title={shareUrl} data-testid="doc-share-url">
          {shareUrl}
        </p>
      )}

      {/* List */}
      <div className="mt-3 space-y-1.5">
        {loading ? (
          <div className="py-3 text-center text-xs text-slate-400">
            <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : docs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">
            No documents yet.
          </div>
        ) : (
          docs.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/50 px-2.5 py-2"
              data-testid={`doc-row-${d.id}`}
            >
              <FileText className="h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-[#0A2540]">{d.filename}</div>
                <div className="text-[10px] text-slate-400">
                  {d.category_label} · {d.uploaded_by}
                </div>
              </div>
              <button
                type="button"
                onClick={() => download(d)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-[#1877F2]"
                data-testid={`doc-download-${d.id}`}
                aria-label="Download"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => remove(d)}
                className="rounded-md p-1.5 text-rose-400 hover:bg-rose-50"
                data-testid={`doc-delete-${d.id}`}
                aria-label="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
